// @flow

import React, { type Node } from 'react';
import styled from 'styled-components';
import { type CardBrand } from '@tectonics/prs/card-brand';
import { useIncomingPayment, getTokenizedCard, getCountry } from '@tectonics/prs/incoming-payment';
import { getLastFourDigits, getBrand } from '@tectonics/prs/card/tokenized';
import { getImageSourceFromBrand } from './images';
import type { Country } from '@tectonics/prs/country';

export function CardInfo(): Node {
  const incomingPayment = useIncomingPayment();

  if (incomingPayment.status !== 'CO')
    throw new Error(`CardInfo: payment status "${incomingPayment.status}" is not valid in this context`);

  const tokenizedCard = getTokenizedCard(incomingPayment);

  if (!tokenizedCard) return null;

  const brand = getBrand(tokenizedCard);
  const lastFourDigits = getLastFourDigits(tokenizedCard);
  const country = getCountry(incomingPayment);

  return (
    <Root>
      <Image src={getImageSourceFromBrand(brand)} alt={brand} />
      <TextBlock>
        <CardNameMessage country={country} brand={brand} />
        <CardDigits country={country} lastFourDigits={lastFourDigits} />
      </TextBlock>
    </Root>
  );
}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 12px 0;

  > * {
    margin: 2px;
  }

  @media (min-width: 840px) {
    flex-direction: row;

    > * {
      margin: 0 12px;
    }
  }
`;

const Image = styled.img`
  height: 30px;
`;

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 0.92em;
  font-weight: 600;
`;

type CardNameMessageProps = {|
  country: Country,
  brand: CardBrand,
|};

function CardNameMessage({ country, brand }: CardNameMessageProps): Node {
  switch (country) {
    case 'br':
      return brazilianCardNameMessage(brand);
    case 'ar':
    case 'bo':
    case 'cl':
    case 'co':
    case 'ec':
    case 'mx':
    case 'pe':
    case 'uy':
    case 'py':
    case 'pa':
    case 'cr':
    case 'sv':
    case 'gt':
    case 'do':
      return spanishCardNameMessage(brand);

    case 'za':
    case 'ke':
    case 'ng':
    case 'eg':
    case 'gh':
    case 'in':
    case 'jm':
      return englishCardNameMessage(brand);
    default:
      throw new Error(`Card-info: No card info message found for country ${country}`);
  }
}

function brazilianCardNameMessage(brand: CardBrand): string {
  switch (brand) {
    case 'amex':
      return 'Cartão de crédito American Express';

    case 'mastercard':
      return 'Cartão de crédito MasterCard';

    default:
      return `Cartão de crédito ${capitalize(brand)}`;
  }
}

function spanishCardNameMessage(brand: CardBrand): string {
  switch (brand) {
    case 'amex':
      return 'Tarjeta de crédito American Express';

    case 'mastercard':
      return 'Tarjeta de crédito MasterCard';

    default:
      return `Tarjeta de crédito ${capitalize(brand)}`;
  }
}

function englishCardNameMessage(brand: CardBrand): string {
  switch (brand) {
    case 'amex':
      return 'American Express credit card';

    case 'mastercard':
      return 'MasterCard credit card';

    default:
      return `${capitalize(brand)} credit card`;
  }
}

type CardDigitsProps = {|
  lastFourDigits: string,
  country: Country,
|};

function CardDigits({ country, lastFourDigits }: CardDigitsProps): Node {
  switch (country) {
    case 'br':
      return (<div>Final {lastFourDigits}</div>);
    case 'ar':
    case 'bo':
    case 'cl':
    case 'co':
    case 'ec':
    case 'mx':
    case 'pe':
    case 'uy':
    case 'py':
    case 'pa':
    case 'cr':
    case 'sv':
    case 'gt':
    case 'do':
      return <div>Finale {lastFourDigits}</div>;
    case 'za':
    case 'ke':
    case 'ng':
    case 'eg':
    case 'gh':
    case 'in':
    case 'jm':
      return <div>Final {lastFourDigits}</div>;
    default:
      throw new Error(`Last Four Digits: no last four digits message found for country ${country}`);
  }
}

function capitalize(text: string): string {
  return text ? text.trim().replace(/^(.)/, (match, value) => value.toUpperCase()) : text;
}
